// Edit mode for every prompt of a tracker: saves the changed rows in one request, opening the child run or refreshing the run.
import { useQueryClient } from '@tanstack/react-query'
import { useId, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ApiError } from '@/lib/api'
import { MAX_EDITS_PER_SAVE, validatePrompts } from '@/lib/promptValidation'
import { useEditPrompts } from '@/lib/queries'
import { useTrackerNavigate } from '@/provider/useTrackerNavigate'
import type { EditPromptsFormProps } from './EditPromptsForm.types'

export function EditPromptsForm({
  runId,
  prompts,
  onDone,
}: EditPromptsFormProps) {
  const [drafts, setDrafts] = useState(prompts)
  const errorId = useId()
  const [serverError, setServerError] = useState<string | null>(null)
  const edit = useEditPrompts(runId)
  const navigate = useTrackerNavigate()
  const queryClient = useQueryClient()
  const errors = useMemo(
    () => validatePrompts(drafts, prompts),
    [drafts, prompts]
  )
  const edits = drafts.flatMap((d, index) =>
    d.trim() === prompts[index].trim() ? [] : [{ index, query: d.trim() }]
  )
  const tooMany = edits.length > MAX_EDITS_PER_SAVE
  const canSave =
    edits.length > 0 && !tooMany && errors.size === 0 && !edit.isPending

  async function save() {
    if (!canSave) return

    setServerError(null)

    try {
      const result = await edit.mutateAsync(edits)

      // A case, spacing, or ending-punctuation fix saves on this run instead of creating a child.
      if (result.runId === runId) {
        await queryClient.invalidateQueries({ queryKey: ['run', runId] })
        onDone()
        return
      }

      navigate(`/run/${result.runId}`)
    } catch (err) {
      setServerError(
        err instanceof ApiError
          ? err.message
          : 'Could not save these prompts. Try again.'
      )
    }
  }

  return (
    <form
      className="flex min-w-0 flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault()
        void save()
      }}
    >
      {drafts.map((draft, i) => {
        const error = errors.get(i)
        const rowErrorId = `${errorId}-${i}`

        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and never reorder
          <div key={i} data-row={i + 1} className="flex min-w-0 flex-col gap-1">
            <div className="flex min-w-0 items-center gap-2">
              <span className="w-6 shrink-0 tabular-nums text-xs text-muted-foreground">
                {String(i + 1).padStart(2, '0')}
              </span>
              <Input
                aria-label={`Prompt ${i + 1}`}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? rowErrorId : undefined}
                autoFocus={i === 0}
                className="h-8"
                value={draft}
                onChange={(e) => {
                  setDrafts((d) =>
                    d.map((v, j) => (j === i ? e.target.value : v))
                  )
                  setServerError(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape' && !edit.isPending) onDone()
                }}
              />
            </div>
            {error && (
              <p id={rowErrorId} className="pl-8 text-xs text-destructive">
                {error}
              </p>
            )}
          </div>
        )
      })}
      {serverError && (
        <p role="alert" className="text-xs text-destructive">
          {serverError}
        </p>
      )}
      {tooMany && (
        <p className="text-xs text-destructive">
          Change {MAX_EDITS_PER_SAVE} or fewer prompts at a time.
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onDone}
          disabled={edit.isPending}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!canSave}>
          Save
        </Button>
      </div>
    </form>
  )
}
